import { Injectable, inject } from '@angular/core';

import { environment } from '../../environments/environment';
import { DateRangeFilter } from '../models/common.models';
import {
  CaseUsageEntry,
  CategoryStockBreakdown,
  ReportSummaryStats,
  StockMovementEntry,
} from '../models/report.models';
import { InventoryItem } from '../models/inventory.models';
import { filterByDateRange } from '../utils/date-range.util';
import { EquipmentService } from './equipment.service';
import { InventoryService } from './inventory.service';
import { DashboardService } from './dashboard.service';

@Injectable({ providedIn: 'root' })
export class ReportService {
  private readonly inventory = inject(InventoryService);
  private readonly equipment = inject(EquipmentService);
  private readonly dashboard = inject(DashboardService);

  /** Current stock snapshot (date range does not apply). */
  getStockOnHandSummary(_range?: DateRangeFilter): ReportSummaryStats {
    const items = this.getStockOnHandItems();
    const locations = new Set(
      this.inventory.getStockSnapshots().map((row) => row.location),
    );
    const categories = new Set(items.map((item) => item.category));
    const totalValue = items.reduce(
      (sum, item) => sum + item.stockLevel * item.price,
      0,
    );

    return {
      totalItems: items.length,
      totalValue,
      categories: categories.size,
      locations: locations.size,
    };
  }

  getStockOnHandByCategory(_range?: DateRangeFilter): CategoryStockBreakdown[] {
    const grouped = new Map<string, CategoryStockBreakdown>();

    for (const item of this.getStockOnHandItems()) {
      const existing = grouped.get(item.category) ?? {
        category: item.category,
        quantity: 0,
        value: 0,
      };

      existing.quantity += item.stockLevel;
      existing.value += item.stockLevel * item.price;
      grouped.set(item.category, existing);
    }

    return [...grouped.values()].sort((a, b) => b.value - a.value);
  }

  /** Current low-stock snapshot (date range does not apply). */
  getLowStockReport(_range?: DateRangeFilter): InventoryItem[] {
    return this.inventory.lowStockItems();
  }

  getStockMovement(range?: DateRangeFilter): StockMovementEntry[] {
    const received = this.inventory
      .getGoodsReceived()
      .filter((note) => note.status !== 'cancelled' && note.status !== 'draft');
    const issues = this.inventory
      .getStockIssues()
      .filter((issue) => issue.status === 'issued');
    const transfers = this.inventory
      .getStockTransfers()
      .filter(
        (transfer) =>
          transfer.status === 'completed' || transfer.status === 'in-transit',
      );

    const movements: StockMovementEntry[] = [
      ...received.map((note) => ({
        id: `grn-${note.id}`,
        date: note.receivedDate,
        itemCode: note.itemName ? '—' : 'GRN',
        itemName: note.itemName?.trim() || `${note.items} item(s) received`,
        movementType: 'received' as const,
        quantity: note.items || 0,
        location:
          [note.branch, note.location].filter(Boolean).join(' / ') ||
          note.supplier ||
          'Receiving',
        reference: note.grnNumber,
      })),
      ...issues.map((issue) => ({
        id: `iss-${issue.id}`,
        date: issue.issueDate,
        itemCode: issue.inventoryItemId ? String(issue.inventoryItemId) : '—',
        itemName:
          issue.itemName?.trim() ||
          `${issue.items} item(s) issued to ${issue.issuedTo || 'recipient'}`,
        movementType: 'issued' as const,
        quantity: issue.items || 0,
        location:
          [issue.branch, issue.location].filter(Boolean).join(' / ') ||
          issue.branch ||
          '—',
        reference: issue.issueNumber,
      })),
      ...transfers.map((transfer) => ({
        id: `trf-${transfer.id || transfer.transferNumber}`,
        date: transfer.transferDate,
        itemCode: '—',
        itemName:
          transfer.itemName?.trim() ||
          `${transfer.items} item(s) transferred`,
        movementType: 'transferred' as const,
        quantity: transfer.items || 0,
        location: `${transfer.fromLocation || '—'} → ${transfer.toLocation || '—'}`,
        reference: transfer.transferNumber,
      })),
    ];

    return filterByDateRange(
      movements.sort((a, b) => (a.date || '').localeCompare(b.date || '')),
      range,
      (row) => row.date,
    );
  }

  /**
   * Case / job usage for the selected period.
   * Uses dashboard upcoming cases plus issued stock grouped by recipient/case ref.
   */
  getCaseUsage(range?: DateRangeFilter): CaseUsageEntry[] {
    const byRef = new Map<string, CaseUsageEntry>();

    const merge = (entry: CaseUsageEntry) => {
      const key = (entry.caseRef || entry.id || '').trim().toLowerCase();
      if (!key) {
        return;
      }
      const existing = byRef.get(key);
      if (!existing) {
        byRef.set(key, { ...entry });
        return;
      }
      existing.itemsIssued = Math.max(
        existing.itemsIssued || 0,
        entry.itemsIssued || 0,
      );
      existing.totalValue = Math.max(
        existing.totalValue || 0,
        entry.totalValue || 0,
      );
      if (!existing.caseName && entry.caseName) {
        existing.caseName = entry.caseName;
      }
      if (!existing.branch && entry.branch) {
        existing.branch = entry.branch;
      }
      if (!existing.date && entry.date) {
        existing.date = entry.date;
      }
      if (!existing.caseType && entry.caseType) {
        existing.caseType = entry.caseType;
      }
    };

    if (!environment.useMockData) {
      for (const row of this.dashboard.caseUsage()) {
        merge(row);
      }
    } else {
      merge({
        id: '1',
        caseRef: 'JOB-2026-0142',
        date: '2026-06-20',
        branch: 'Sandton',
        itemsIssued: 12,
        totalValue: 4850,
        caseType: 'Event',
      });
      merge({
        id: '2',
        caseRef: 'ORD-2026-0143',
        date: '2026-06-22',
        branch: 'Pretoria',
        itemsIssued: 8,
        totalValue: 3120,
        caseType: 'Internal',
      });
    }

    for (const issue of this.inventory
      .getStockIssues()
      .filter((row) => row.status === 'issued')) {
      const caseRef = (issue.issuedTo || '').trim() || issue.issueNumber;
      merge({
        id: `issue-${issue.id}`,
        caseRef,
        caseName: issue.issuedTo || issue.itemName || caseRef,
        date: issue.issueDate,
        branch: issue.branch || '',
        itemsIssued: issue.items || 0,
        totalValue: issue.totalValue || 0,
        caseType: 'Issue',
      });
    }

    const entries = [...byRef.values()].sort((a, b) =>
      (a.date || '').localeCompare(b.date || ''),
    );

    return filterByDateRange(entries, range, (row) => row.date);
  }

  /** @deprecated Use getCaseUsage */
  getFuneralUsage(range?: DateRangeFilter): CaseUsageEntry[] {
    return this.getCaseUsage(range);
  }

  getCheckedOutEquipmentReport(range?: DateRangeFilter) {
    const rows = this.equipment
      .getCheckouts()
      .filter((row) => row.status === 'active' || row.status === 'overdue');

    return filterByDateRange(rows, range, (row) => row.checkoutDate);
  }

  getOverdueReturnsReport(range?: DateRangeFilter) {
    const overdue = this.equipment.getOverdueCheckouts();
    // Prefer due-date filter; if none fall in range, still show all current overdue.
    const filtered = filterByDateRange(overdue, range, (row) => row.dueDate);
    return filtered.length ? filtered : overdue;
  }

  getDamagedEquipmentReport(range?: DateRangeFilter) {
    return filterByDateRange(
      this.equipment.getDamagedRecords(),
      range,
      (row) => row.reportedDate,
    );
  }

  /** Current valuation snapshot (date range does not apply). */
  getStockValuation(_range?: DateRangeFilter) {
    return this.getStockOnHandItems().map((item) => ({
      itemCode: item.code,
      itemName: item.name,
      category: item.category,
      quantity: item.stockLevel,
      unitCost: item.price,
      totalValue: item.stockLevel * item.price,
    }));
  }

  private getStockOnHandItems(): InventoryItem[] {
    return this.inventory.allItems().filter((item) => item.status !== 'inactive');
  }
}
